import React from 'react';
import styled from 'styled-components';
import { Popover } from 'antd';
import { colors } from '../../design-system/tokens/colors';
import type { CellData } from './types';

interface PendingChangePopoverProps {
  cellData?: CellData; // For AI suggesting update to existing value
  // OR manual edit scenario:
  currentValue?: string;
  newValue?: string;
  aiReasoning?: string;
  mode?: 'ai-suggests' | 'user-overwrite'; // Which scenario
  onAccept: () => void;
  onDismiss: () => void;
  children: React.ReactElement;
  open?: boolean; // Control visibility externally for user-overwrite mode
  onOpenChange?: (open: boolean) => void;
}

const PopoverContent = styled.div`
  width: 360px;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const PopoverHeader = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.border.secondary};
  flex-shrink: 0;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
  }
`;

const ValueSection = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ValueLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: 8px;
`;

const ValueBox = styled.div<{ $highlighted?: boolean }>`
  background: ${props => props.$highlighted ? 'rgba(251, 191, 36, 0.08)' : '#f8f9fa'};
  border: ${props => props.$highlighted ? '1px solid rgba(251, 191, 36, 0.3)' : 'none'};
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: ${colors.text.primary};
  line-height: 1.5;
  max-height: 120px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
  }
`;

const ReasoningSection = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ReasoningLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: 8px;
`;

const ReasoningText = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  line-height: 1.5;
  padding: 10px 12px;
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 6px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${colors.border.secondary};
  flex-shrink: 0;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'text' }>`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${({ $variant = 'secondary' }) => {
    if ($variant === 'primary') {
      return `
        background: ${colors.brand.primary};
        color: white;
        &:hover {
          background: ${colors.brand.primaryHover};
        }
      `;
    } else if ($variant === 'text') {
      return `
        background: transparent;
        color: ${colors.text.secondary};
        padding: 6px 8px;
        &:hover {
          color: ${colors.text.primary};
          background: rgba(0, 0, 0, 0.03);
        }
      `;
    } else {
      return `
        background: white;
        color: ${colors.text.primary};
        border: 1px solid ${colors.border.secondary};
        &:hover {
          background: ${colors.background.secondary};
        }
      `;
    }
  }}
`;

export const PendingChangePopover: React.FC<PendingChangePopoverProps> = ({
  cellData,
  currentValue,
  newValue,
  aiReasoning,
  mode = 'ai-suggests',
  onAccept,
  onDismiss,
  children,
  open,
  onOpenChange
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  
  // Determine values based on mode
  const isAISuggest = mode === 'ai-suggests';
  const header = isAISuggest ? 'AI Suggests Update' : 'Confirm Overwrite';
  
  const oldValue = isAISuggest 
    ? (cellData?.value || '(empty)') 
    : (currentValue || '(empty)');
  
  const suggestedValue = isAISuggest 
    ? (cellData?.pending_change?.value || '(no suggestion)')
    : (newValue || '(empty)');
  
  const reasoning = isAISuggest 
    ? cellData?.pending_change?.reasoning 
    : aiReasoning;

  const oldLabel = isAISuggest ? 'Current Value' : 'Current AI Value';
  const newLabel = isAISuggest ? 'Suggested Value' : 'Your New Value';
  const acceptLabel = isAISuggest ? 'Accept' : 'Confirm';
  const dismissLabel = isAISuggest ? 'Dismiss' : 'Cancel';

  const handleAccept = () => {
    setInternalOpen(false);
    onAccept();
  };

  const handleDismiss = () => {
    setInternalOpen(false);
    onDismiss();
  };

  const handleOpenChange = (newOpen: boolean) => {
    setInternalOpen(newOpen);
    // Only call the external onOpenChange if provided (for user-overwrite mode)
    // For AI-suggests mode, we don't pass onOpenChange, so clicking outside just closes
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
  };

  const content = (
    <PopoverContent onClick={(e) => e.stopPropagation()}>
      <PopoverHeader>
        {header}
      </PopoverHeader>

      <ScrollableContent>
        <ValueSection>
          <ValueLabel>{oldLabel}</ValueLabel>
          <ValueBox>{oldValue}</ValueBox>
        </ValueSection>

        <ValueSection>
          <ValueLabel>{newLabel}</ValueLabel>
          <ValueBox $highlighted>
            {suggestedValue}
          </ValueBox>
        </ValueSection>

        {reasoning && (
          <ReasoningSection>
            <ReasoningLabel>Reasoning</ReasoningLabel>
            <ReasoningText>
              {reasoning}
            </ReasoningText>
          </ReasoningSection>
        )}
      </ScrollableContent>

      <ButtonGroup>
        <Button 
          $variant="text"
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
        >
          {dismissLabel}
        </Button>
        <Button 
          $variant="primary"
          onClick={(e) => {
            e.stopPropagation();
            handleAccept();
          }}
        >
          {acceptLabel}
        </Button>
      </ButtonGroup>
    </PopoverContent>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomLeft"
      overlayStyle={{ zIndex: 1100 }}
      open={open !== undefined ? open : internalOpen}
      onOpenChange={handleOpenChange}
    >
      {children}
    </Popover>
  );
};

